import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const SIDE_IMG = "https://images.pexels.com/photos/18051278/pexels-photo-18051278.jpeg";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 6) return setError("Password must be at least 6 characters.");
    if (pwd !== confirm) return setError("Passwords do not match.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen grid md:grid-cols-2" data-testid="reset-password-page">
      <div className="flex items-center justify-center px-6 py-24 md:py-0 order-2 md:order-1">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="w-full max-w-sm">
          <p className="label text-accent mb-4">/ New password</p>
          <h1 className="font-display text-4xl font-light tracking-tight mb-10">Choose a new password</h1>

          {!token && (
            <p className="mb-6 text-sm text-destructive border border-destructive px-4 py-3" data-testid="reset-missing-token">
              Missing or invalid reset link. Request a new one from the <Link to="/forgot-password" className="border-b border-destructive">forgot password</Link> page.
            </p>
          )}

          {done ? (
            <div className="border border-border p-6 flex flex-col gap-4" data-testid="reset-success">
              <div className="h-12 w-12 flex items-center justify-center bg-accent text-accent-foreground"><Check size={22} strokeWidth={1.5} /></div>
              <p className="text-sm text-foreground/80">Password updated. Redirecting you to sign in…</p>
            </div>
          ) : token && (
            <>
              {error && <p className="mb-6 text-sm text-destructive border border-destructive px-4 py-3" data-testid="reset-error">{error}</p>}
              <form onSubmit={submit} className="space-y-6" data-testid="reset-form">
                <div>
                  <label className="label text-muted-foreground block mb-2">New password</label>
                  <input type="password" required minLength={6} value={pwd} onChange={(e) => setPwd(e.target.value)} className="w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-accent transition-colors" data-testid="reset-password-input" />
                  <p className="mt-2 text-xs text-muted-foreground">At least 6 characters.</p>
                </div>
                <div>
                  <label className="label text-muted-foreground block mb-2">Confirm password</label>
                  <input type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-accent transition-colors" data-testid="reset-confirm-input" />
                </div>
                <button type="submit" disabled={busy} className="w-full h-14 bg-primary text-primary-foreground label hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="reset-submit">
                  {busy ? "Updating…" : "Update password"} <ArrowRight size={16} strokeWidth={1.5} />
                </button>
              </form>
            </>
          )}

          <p className="mt-8 text-sm text-muted-foreground">
            <Link to="/login" className="text-accent border-b border-accent" data-testid="to-login">Back to sign in</Link>
          </p>
        </motion.div>
      </div>

      <div className="relative hidden md:block bg-primary overflow-hidden order-1 md:order-2">
        <img src={SIDE_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-primary/50" />
        <div className="relative h-full flex flex-col justify-between p-12 text-primary-foreground text-right items-end">
          <span className="font-display text-2xl">Coastal Chemistry</span>
          <h2 className="font-display text-5xl font-light leading-[0.95] tracking-tight max-w-sm">A fresh start, same lab.</h2>
        </div>
      </div>
    </main>
  );
}
